import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Heart, LogOut, LayoutDashboard, Menu, X, Moon, Sun } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { User } from "@supabase/supabase-js";

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout = ({ children }: LayoutProps) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    // Check for saved theme preference or default to light mode
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const shouldBeDark = savedTheme === 'dark' || (!savedTheme && prefersDark);
    
    setIsDark(shouldBeDark);
    if (shouldBeDark) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const toggleTheme = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    
    if (newIsDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error("Fehler beim Abmelden");
    } else {
      toast.success("Erfolgreich abgemeldet");
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b glass backdrop-blur-lg">
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src="/mediqr-logo.png" alt="MediQR Logo" className="h-8 w-8" />
              <span className="text-xl font-bold gradient-text">MediQR</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/how-it-works" className="text-sm font-medium hover:text-primary transition-colors">
                So funktioniert's
              </Link>
              <Link to="/pricing" className="text-sm font-medium hover:text-primary transition-colors">
                Preise
              </Link>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="hover:bg-primary/10"
              >
                {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
              
              {user ? (
                <>
                  <Link to="/dashboard">
                    <Button variant="outline" size="sm">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Dashboard
                    </Button>
                  </Link>
                  <Button variant="ghost" size="sm" onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Abmelden
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/auth">
                    <Button variant="outline" size="sm">
                      Anmelden
                    </Button>
                  </Link>
                  <Link to="/create">
                    <Button size="sm" className="bg-gradient-primary hover:shadow-glow">
                      Profil erstellen
                    </Button>
                  </Link>
                </>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t space-y-3">
              <Link
                to="/how-it-works"
                className="block text-sm font-medium hover:text-primary transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                So funktioniert's
              </Link>
              <Link
                to="/pricing"
                className="block text-sm font-medium hover:text-primary transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Preise
              </Link>
              
              <Button
                variant="outline"
                size="sm"
                onClick={toggleTheme}
                className="w-full justify-start"
              >
                {isDark ? (
                  <>
                    <Sun className="mr-2 h-4 w-4" />
                    Hell
                  </>
                ) : (
                  <>
                    <Moon className="mr-2 h-4 w-4" />
                    Dunkel
                  </>
                )}
              </Button>
              
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" size="sm" className="w-full justify-start">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Dashboard
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleSignOut();
                    }}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Abmelden
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" size="sm" className="w-full">
                      Anmelden
                    </Button>
                  </Link>
                  <Link to="/create" onClick={() => setMobileMenuOpen(false)}>
                    <Button size="sm" className="w-full bg-gradient-primary hover:shadow-glow">
                      Profil erstellen
                    </Button>
                  </Link>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t glass backdrop-blur-lg mt-auto">
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <img src="/mediqr-logo.png" alt="MediQR Logo" className="h-8 w-8" />
                <span className="text-xl font-bold gradient-text">MediQR</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Ihre medizinischen Notfalldaten - immer griffbereit
              </p>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Mit</span>
                <Heart className="h-4 w-4 text-primary fill-primary" />
                <span>entwickelt von <a href="https://diekerit.com?referer=mediqr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">DiekerIT</a></span>
              </div>
            </div>

            {/* Product */}
            <div>
              <h3 className="font-semibold mb-4">Produkt</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/how-it-works" className="text-muted-foreground hover:text-primary transition-colors">
                    So funktioniert's
                  </Link>
                </li>
                <li>
                  <Link to="/pricing" className="text-muted-foreground hover:text-primary transition-colors">
                    Preise
                  </Link>
                </li>
                <li>
                  <Link to="/install" className="text-muted-foreground hover:text-primary transition-colors">
                    App installieren
                  </Link>
                </li>
              </ul>
            </div>

            {/* Account */}
            <div>
              <h3 className="font-semibold mb-4">Account</h3>
              <ul className="space-y-2 text-sm">
                {user ? (
                  <>
                    <li>
                      <Link to="/dashboard" className="text-muted-foreground hover:text-primary transition-colors">
                        Dashboard
                      </Link>
                    </li>
                    <li>
                      <Link to="/create" className="text-muted-foreground hover:text-primary transition-colors">
                        Profil erstellen
                      </Link>
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      <Link to="/auth" className="text-muted-foreground hover:text-primary transition-colors">
                        Anmelden
                      </Link>
                    </li>
                    <li>
                      <Link to="/create" className="text-muted-foreground hover:text-primary transition-colors">
                        Registrieren
                      </Link>
                    </li>
                  </>
                )}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="font-semibold mb-4">Rechtliches</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/impressum" className="text-muted-foreground hover:text-primary transition-colors">
                    Impressum
                  </Link>
                </li>
                <li>
                  <Link to="/datenschutz" className="text-muted-foreground hover:text-primary transition-colors">
                    Datenschutz
                  </Link>
                </li>
                <li>
                  <Link to="/agb" className="text-muted-foreground hover:text-primary transition-colors">
                    AGB
                  </Link>
                </li>
                <li>
                  <Link to="/widerrufsrecht" className="text-muted-foreground hover:text-primary transition-colors">
                    Widerrufsrecht
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} MediQR by <a href="https://diekerit.com?referer=mediqr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">DiekerIT</a>. Alle Rechte vorbehalten.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
